import { Component } from 'react';
import { createRecordingDialogEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { JitsiRecordingConstants } from '../../../base/lib-jitsi-meet';
import { setVideoMuted } from '../../../base/media/actions';
import { setRequestingSubtitles } from '../../../subtitles/actions.any';
import { isRecorderTranscriptionsRunning } from '../../../transcribing/functions';
import { setStopRecordingIntent, stopLocalVideoRecording } from '../../actions';
import { RECORDING_METADATA_ID } from '../../constants';
import { getActiveSession } from '../../functions';
import LocalRecordingManager from './LocalRecordingManager';
/**
 * Abstract React Component for getting confirmation to stop a file recording
 * session in progress.
 *
 * @augments Component
 */
export default class AbstractStopRecordingDialog extends Component {
    /**
     * Initializes a new {@code AbstrStopRecordingDialog} instance.
     *
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        // Bind event handler so it is only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
        this._toggleScreenshotCapture = this._toggleScreenshotCapture.bind(this);
    }
    /**
     * Stops the recording session.
     *
     * @private
     * @returns {boolean} - True (to note that the modal should be closed).
     */
    _onSubmit() {
        sendAnalytics(createRecordingDialogEvent('stop', 'confirm.button'));
        const { _conference, _displaySubtitles, _fileRecordingSession, _localRecording, _subtitlesLanguage, _transcriptionRunning, dispatch, localRecordingVideoStop, stopMode } = this.props;
        const stoppingRecording = stopMode === 'recording';
        const stoppingTranscription = stopMode === 'transcription';
        // Pre-seed stopRecordingIntent so the off-sound/notification coordinator
        // (maybeNotifyRecordingStop) knows what to wait for. Each button only
        // signals the service it is responsible for stopping.
        if (!_localRecording) {
            const recordingRunning = stoppingRecording && Boolean(_fileRecordingSession);
            const transcriptionRunning = stoppingTranscription && _transcriptionRunning;
            if (recordingRunning || transcriptionRunning) {
                dispatch(setStopRecordingIntent({
                    recording: recordingRunning,
                    transcription: transcriptionRunning
                }));
            }
        }
        if (stoppingRecording) {
            if (_localRecording) {
                dispatch(stopLocalVideoRecording());
                if (localRecordingVideoStop) {
                    dispatch(setVideoMuted(true));
                }
            }
            else if (_fileRecordingSession) {
                _conference?.stopRecording(_fileRecordingSession.id);
                this._toggleScreenshotCapture();
            }
            _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                isRecordingRequested: false
            });
        }
        if (stoppingTranscription) {
            // TODO: this should be an action in transcribing. -saghul
            dispatch(setRequestingSubtitles(Boolean(_displaySubtitles), _displaySubtitles, _subtitlesLanguage, true));
            _conference?.getMetadataHandler().setMetadata(RECORDING_METADATA_ID, {
                isTranscribingEnabled: false
            });
        }
        return true;
    }
    /**
     * Toggles screenshot capture feature.
     *
     * @returns {void}
     */
    _toggleScreenshotCapture() {
        // To be implemented by subclass.
    }
}
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code StopRecordingDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {IProps}
 */
export function _mapStateToProps(state) {
    const { _displaySubtitles, _language: _subtitlesLanguage } = state['features/subtitles'];
    return {
        _conference: state['features/base/conference'].conference,
        _displaySubtitles,
        _fileRecordingSession: getActiveSession(state, JitsiRecordingConstants.mode.FILE),
        _localRecording: LocalRecordingManager.isRecordingLocally(),
        _subtitlesLanguage,
        _transcriptionRunning: isRecorderTranscriptionsRunning(state)
    };
}
